/**
 * AdminSvCodesPage — SV-Code-Referenz (Iter 19.63.91).
 *
 * Read-only Übersicht aller österreichischen Sozialversicherungs-Codes,
 * die das Backend bei der Vordienstzeiten-Klassifikation deterministisch
 * verarbeitet (GSVG, BSVG, FSVG + § 19a/§ 16/§ 17 ASVG).
 *
 * Quelle: GET /api/tool/sv-codes (kein Auth — Referenz-Daten).
 * Single Source of Truth: /app/backend/sv_codes.json.
 *
 * Zielgruppe: Sachbearbeiter & Admins, die nachvollziehen möchten,
 * warum eine Vordienst-Zeile als ANR oder nicht-ANR klassifiziert wurde.
 */
import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, extractError } from "../lib/api";
import {
  CheckCircle,
  XCircle,
  Warning,
  Info,
  Code,
  ArrowsClockwise,
  ClipboardText,
} from "@phosphor-icons/react";


// ─── Renderers ────────────────────────────────────────────────────────────

function CategoryCard({ category, kind }) {
  // kind: "anrechenbar" | "nicht_anrechenbar"
  const isPos = kind === "anrechenbar";
  const accent = isPos
    ? "border-emerald-200 bg-emerald-50/40"
    : "border-red-200 bg-red-50/40";
  const badgeCls = isPos
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : "bg-red-100 text-red-800 border-red-300";
  const factor = category.default_faktor ?? (isPos ? 1.0 : 0.0);
  return (
    <div
      data-testid={`sv-code-category-${category.id}`}
      className={[
        "rounded-sm border bg-white p-5 transition-crisp",
        accent,
      ].join(" ")}
    >
      <div className="flex items-start gap-3 mb-3">
        {isPos ? (
          <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
        ) : (
          <XCircle size={22} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-cabinet text-lg font-medium text-ink-900 leading-tight">
            {category.label}
          </h3>
          <code className="block text-[10px] text-warm-500 mt-1 font-mono tabular-nums">
            id: {category.id}
          </code>
        </div>
        <span
          className={[
            "shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider",
            badgeCls,
          ].join(" ")}
        >
          Faktor {String(factor).replace(".", ",")}
        </span>
      </div>

      <p className="text-sm text-warm-800 mb-4 leading-relaxed">
        {category.description}
      </p>

      {Array.isArray(category.example_codes) && category.example_codes.length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
            Beispiel-Codes aus dem SV-Auszug
          </div>
          <ul className="space-y-1">
            {category.example_codes.map((code, idx) => (
              <li
                key={idx}
                className="font-mono text-xs text-ink-800 bg-white border border-ink-200 rounded-sm px-2 py-1 break-words"
              >
                {code}
              </li>
            ))}
          </ul>
        </div>
      )}

      {category.begruendung_template && (
        <div className="border-t border-ink-200 pt-3 mt-3">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1.5">
            Begründungs-Template
          </div>
          <blockquote className="text-xs italic text-warm-700 border-l-2 border-ink-300 pl-3">
            &bdquo;{category.begruendung_template}&ldquo;
          </blockquote>
        </div>
      )}
    </div>
  );
}


function AntiPatternRow({ pattern }) {
  return (
    <li
      data-testid={`sv-code-anti-pattern-${pattern.id}`}
      className="rounded-sm border border-amber-200 bg-amber-50/50 p-4"
    >
      <div className="flex items-start gap-2.5">
        <Warning
          size={18}
          weight="fill"
          className="text-amber-600 shrink-0 mt-0.5"
        />
        <div className="flex-1 min-w-0 space-y-1.5">
          <code className="block text-xs text-amber-900 font-mono leading-snug break-words">
            {pattern.text}
          </code>
          <p className="text-xs text-warm-800 leading-relaxed">
            <strong className="text-amber-900">Falsch:</strong>{" "}
            {pattern.explanation}
          </p>
        </div>
      </div>
    </li>
  );
}


function CopyButton({ value, label = "Kopieren" }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silently ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid="sv-code-copy-btn"
      className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-ink-300 bg-white text-[10px] font-medium text-ink-700 hover:bg-ink-50 transition-crisp"
      title="In die Zwischenablage kopieren"
    >
      <ClipboardText size={11} weight="bold" />
      {copied ? "Kopiert" : label}
    </button>
  );
}


// ─── Page Component ───────────────────────────────────────────────────────

export default function AdminSvCodesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await api.get("/tool/sv-codes");
      setData(resp.data);
    } catch (err) {
      setError(extractError(err, "SV-Codes konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const counts = useMemo(() => {
    if (!data) return { anr: 0, nicht: 0, anti: 0, patterns: 0 };
    return {
      anr: (data.anrechenbar || []).length,
      nicht: (data.nicht_anrechenbar || []).length,
      anti: (data.anti_patterns || []).length,
      patterns: (data.fe_detection_patterns || []).length,
    };
  }, [data]);

  return (
    <AdminLayout
      title="SV-Code-Referenz"
      subtitle="Read-only Übersicht aller österreichischen Sozialversicherungs-Codes, die das Tool bei der Vordienstzeiten-Klassifikation deterministisch verarbeitet. Quelle: /app/backend/sv_codes.json (Single Source of Truth)."
      actions={
        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          data-testid="sv-codes-refresh-btn"
          className="inline-flex items-center gap-1.5 rounded-sm border border-ink-300 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 transition-crisp hover:bg-ink-50 disabled:opacity-60"
        >
          <ArrowsClockwise size={13} weight="bold" />
          {loading ? "Lade…" : "Aktualisieren"}
        </button>
      }
    >
      <div data-testid="sv-codes-page" className="space-y-10">
        {error && (
          <div
            className="rounded-sm border border-red-300 bg-red-50 p-4 text-sm text-red-800"
            data-testid="sv-codes-error"
          >
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="rounded-sm border border-ink-200 bg-white p-6 text-sm text-warm-700">
            Lade SV-Codes…
          </div>
        )}

        {data && (
          <>
            {/* ── Kompakte Stats ─────────────────────────────────────── */}
            <section
              data-testid="sv-codes-stats"
              className="grid grid-cols-2 md:grid-cols-4 gap-3"
            >
              <div className="rounded-sm border border-emerald-200 bg-emerald-50/50 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">
                  Anrechenbar
                </div>
                <div className="font-cabinet text-2xl font-medium text-emerald-900 tabular-nums">
                  {counts.anr}
                </div>
                <div className="text-xs text-warm-600">Kategorien</div>
              </div>
              <div className="rounded-sm border border-red-200 bg-red-50/50 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-red-700">
                  Nicht anrechenbar
                </div>
                <div className="font-cabinet text-2xl font-medium text-red-900 tabular-nums">
                  {counts.nicht}
                </div>
                <div className="text-xs text-warm-600">Kategorien</div>
              </div>
              <div className="rounded-sm border border-amber-200 bg-amber-50/50 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">
                  Anti-Patterns
                </div>
                <div className="font-cabinet text-2xl font-medium text-amber-900 tabular-nums">
                  {counts.anti}
                </div>
                <div className="text-xs text-warm-600">Falsch-Klassifikationen</div>
              </div>
              <div className="rounded-sm border border-ink-200 bg-white px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                  FE-Patterns
                </div>
                <div className="font-cabinet text-2xl font-medium text-ink-900 tabular-nums">
                  {counts.patterns}
                </div>
                <div className="text-xs text-warm-600">Frontend-Regex</div>
              </div>
            </section>

            {/* ── Info-Banner ─────────────────────────────────────────── */}
            <section
              data-testid="sv-codes-info-banner"
              className="rounded-sm border border-ink-200 bg-ink-50/60 p-4 flex items-start gap-3"
            >
              <Info size={18} weight="bold" className="text-brand-500 shrink-0 mt-0.5" />
              <div className="text-xs text-warm-800 leading-relaxed">
                <strong className="text-ink-900">Verwendung:</strong>{" "}
                Diese Tabelle ist die <em>Single Source of Truth</em> für den
                Backend-Prompt (LLM-Disambiguierung) und das Frontend
                (Regel-Quelle &bdquo;KV-Regel&ldquo; vs. &bdquo;ANNAHME&ldquo;). Pytest verifiziert
                Konsistenz beider Stellen — siehe{" "}
                <code className="font-mono text-[11px] text-ink-900">
                  test_iter1963_91_sv_codes_source_of_truth.py
                </code>
                . Änderungen an SV-Codes erfolgen ausschließlich über{" "}
                <code className="font-mono text-[11px] text-ink-900">
                  /app/backend/sv_codes.json
                </code>
                .
                {data.iter_origin && (
                  <span className="text-warm-600">
                    {" "}· Schema-Version: <code className="font-mono">{data.iter_origin}</code>
                  </span>
                )}
              </div>
            </section>

            {/* ── Anrechenbar ─────────────────────────────────────────── */}
            <section data-testid="sv-codes-anrechenbar-section">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-cabinet text-2xl text-ink-900 font-medium leading-none">
                  Anrechenbare SV-Codes
                </h2>
                <span className="text-xs text-warm-500">
                  faktor &gt; 0 — zählen auf die Vordienstzeit
                </span>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {(data.anrechenbar || []).map((cat) => (
                  <CategoryCard key={cat.id} category={cat} kind="anrechenbar" />
                ))}
              </div>
            </section>

            {/* ── Nicht anrechenbar ───────────────────────────────────── */}
            <section data-testid="sv-codes-nicht-anrechenbar-section">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-cabinet text-2xl text-ink-900 font-medium leading-none">
                  Nicht anrechenbare SV-Codes
                </h2>
                <span className="text-xs text-warm-500">
                  faktor = 0 — keine Erwerbstätigkeit im Sinne der KV-Regel
                </span>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {(data.nicht_anrechenbar || []).map((cat) => (
                  <CategoryCard key={cat.id} category={cat} kind="nicht_anrechenbar" />
                ))}
              </div>
            </section>

            {/* ── Anti-Patterns ───────────────────────────────────────── */}
            {Array.isArray(data.anti_patterns) && data.anti_patterns.length > 0 && (
              <section data-testid="sv-codes-anti-patterns-section">
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="font-cabinet text-2xl text-ink-900 font-medium leading-none">
                    Anti-Patterns
                  </h2>
                  <span className="text-xs text-warm-500">
                    Häufige Fehl-Klassifikationen, die das Tool aktiv verhindert
                  </span>
                </div>
                <ul className="space-y-3" data-testid="sv-codes-anti-patterns-list">
                  {data.anti_patterns.map((p) => (
                    <AntiPatternRow key={p.id} pattern={p} />
                  ))}
                </ul>
              </section>
            )}

            {/* ── Korrekte Patterns ───────────────────────────────────── */}
            {Array.isArray(data.korrekte_patterns) && data.korrekte_patterns.length > 0 && (
              <section data-testid="sv-codes-korrekte-patterns-section">
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="font-cabinet text-2xl text-ink-900 font-medium leading-none">
                    Korrekte Begründungs-Patterns
                  </h2>
                  <span className="text-xs text-warm-500">
                    So sollten Begründungen formuliert sein
                  </span>
                </div>
                <ul className="space-y-2.5" data-testid="sv-codes-korrekte-patterns-list">
                  {data.korrekte_patterns.map((p, idx) => (
                    <li
                      key={idx}
                      className="rounded-sm border border-emerald-200 bg-emerald-50/40 p-3.5 flex items-start gap-2.5"
                      data-testid={`sv-code-korrekt-pattern-${idx}`}
                    >
                      <CheckCircle
                        size={16}
                        weight="fill"
                        className="text-emerald-600 shrink-0 mt-0.5"
                      />
                      <code className="font-mono text-xs text-ink-800 leading-snug break-words">
                        {p}
                      </code>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* ── FE-Detection-Patterns (Dev/Tech) ────────────────────── */}
            {Array.isArray(data.fe_detection_patterns) && data.fe_detection_patterns.length > 0 && (
              <section data-testid="sv-codes-fe-patterns-section">
                <details className="rounded-sm border border-ink-200 bg-white">
                  <summary
                    className="cursor-pointer px-4 py-3 text-sm font-medium text-ink-800 hover:bg-ink-50 transition-crisp flex items-center gap-2"
                    data-testid="sv-codes-fe-patterns-toggle"
                  >
                    <Code size={14} weight="bold" className="text-warm-500" />
                    Frontend-Erkennungs-Patterns ({data.fe_detection_patterns.length})
                    <span className="text-xs text-warm-500 font-normal">
                      — technisch, für Entwickler
                    </span>
                  </summary>
                  <div className="px-4 py-4 border-t border-ink-200 space-y-3">
                    {data.fe_detection_patterns.map((p) => (
                      <div
                        key={p.id}
                        data-testid={`sv-code-fe-pattern-${p.id}`}
                        className="rounded-sm border border-ink-200 bg-ink-50/40 p-3"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <code className="font-mono text-[10px] text-warm-500 uppercase tracking-wider">
                            {p.id}
                          </code>
                          <CopyButton value={p.regex} label="Regex" />
                        </div>
                        <code className="block font-mono text-xs text-ink-900 bg-white border border-ink-200 rounded-sm px-2 py-1.5 mb-2 break-all">
                          /{p.regex}/{p.flags}
                        </code>
                        {p.description && (
                          <p className="text-xs text-warm-700 leading-relaxed">
                            {p.description}
                          </p>
                        )}
                        {Array.isArray(p.example_matches) && p.example_matches.length > 0 && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                              Matcht:
                            </span>
                            {p.example_matches.map((m, idx) => (
                              <code
                                key={idx}
                                className="font-mono text-[11px] text-ink-800 bg-white border border-ink-200 rounded-sm px-1.5 py-0.5"
                              >
                                {m}
                              </code>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              </section>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
